import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import { PreloadAllModules, Router, provideRouter, withPreloading, withRouterConfig } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { appRouterConfig } from '@app/app.router-config';
import { appRoutes } from '@app/app.routing';
import { tenantsRoutes } from '@app/sub-domain-tenants.route';
import { AuthInterceptor } from '@core/auth/auth.interceptor';
import { AuthService } from '@core/auth/auth.service';
import { appConfig as fuseAppConfig } from '@core/config/app.config';
import { provideIcons } from '@core/icons/icons.provider';
import { environment } from '@environments/environments';
import { provideFuseMockApi } from '@fuse/lib/mock-api/mock-api.provider';
import { FUSE_APP_CONFIG } from '@fuse/services/config/config.constants';
import { FuseSplashScreenService } from '@fuse/services/splash-screen/splash-screen.service';
import { mockApiServices } from '@mock-api/index';
import { OfficeTenantsService } from '@office/tenants/office-tenants.service';
import { createFormlyProviders } from '@providers/formly.providers';
import { createFuseProviders } from '@providers/fuse.providers';
import { momentAdapterFactory } from '@providers/moment-adapter.factory';
import { createQuillProviders } from '@providers/quill.providers';
import { APP_ROUTES, TENANTS_ROUTES } from '@providers/route.tokens';
import { TenantView } from '@shared/models/domain/domain-view.model';
import { PageResponse } from '@shared/models/response/page-response.model';
import { DateAdapter, provideCalendar } from 'angular-calendar';
import { NgxMaskConfig, provideEnvironmentNgxMask, provideNgxMask } from 'ngx-mask';
import { lastValueFrom } from 'rxjs';

const initApp = (
    officeTenantsService: OfficeTenantsService,
    router: Router
) => {
    return (): Promise<PageResponse<TenantView> | undefined> =>
        lastValueFrom<PageResponse<TenantView>, PageResponse<TenantView>>(
            officeTenantsService.getTenants({
                itemsPerPage: environment.defaultItemsPerPage,
                sort: 'dateCreated',
                sortDir: 'desc',
            }),
            { defaultValue: undefined },
        )
            .then((response) => {
                const websiteSlug =
                    officeTenantsService.findSlugNameBaseOnRoute();
                if (websiteSlug) router.config.splice(-1, 0, ...tenantsRoutes);
                return response;
            });
};

const maskConfig: Partial<NgxMaskConfig> = {
    validation: false,
};

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(
            ReactiveFormsModule,
            ...createFormlyProviders(),
        ),
        ...createQuillProviders(),
        provideCalendar({
            provide: DateAdapter,
            useFactory: momentAdapterFactory,
        }),
        provideServiceWorker('sw.js', {
            enabled: environment.production,
            // Register the ServiceWorker as soon as the application is stable or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000',
        }),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withRouterConfig(appRouterConfig)
        ),
        { provide: APP_ROUTES, useValue: appRoutes },
        { provide: TENANTS_ROUTES, useValue: tenantsRoutes },
        ...createFuseProviders(),
        ...provideIcons(),
        ...provideFuseMockApi(mockApiServices),
        provideEnvironmentNgxMask(maskConfig),
        provideNgxMask(),
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideAppInitializer(() => {
            inject(FuseSplashScreenService);
            const officeTenantsService = inject(OfficeTenantsService);
            const router = inject(Router);
            const initializerFn = initApp(
                officeTenantsService,
                router
            );
            return initializerFn();
        }),
        provideNativeDateAdapter(),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
        AuthService,
        DatePipe,
        {
            provide: FUSE_APP_CONFIG,
            useValue: fuseAppConfig
        }
    ],
};
